import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, Pencil, Plus, X } from 'lucide-react';
import {
  OverheadExpense,
  OverheadFrequency,
  OVERHEAD_CATEGORY_LABELS,
  OVERHEAD_FREQUENCIES,
  OVERHEAD_FREQUENCY_LABELS,
  formatOverheadBillOn,
  resolveOverheadRecurringDay,
  resolveOverheadRenewalMonth,
} from '../../types/finances';
import type { PurchaseOverheadGroup } from '../../types/purchasing';
import { overheadMonthlyContribution } from '../../utils/financesForecast';
import {
  emptyOpexBudgets,
  formatOpexBudgetAmount,
  OPEX_BUDGET_INTERVALS,
  budgetedMonthlyEquivalent,
  opexBudgetIntervalMeta,
  totalBudgetedOpexMonthly,
  type OpexBudgets,
} from '../../utils/opexBudgets';
import type { OpexBudgetInterval } from '../../utils/opexBudgetMath';
import { PURCHASE_OVERHEAD_GROUPS, isPurchaseOverheadGroup } from '../../utils/purchasingSpendClass';
import {
  OVERHEAD_DATE_PRESETS,
  OVERHEAD_DAY_BUCKETS,
  OverheadDatePreset,
  OverheadDayBucket,
  filterOverheadByDate,
  formatOccurrenceSummary,
  formatOverheadRangeLabel,
  occurrenceAmount,
  fromInputDate,
  overheadOccurrencesInRange,
  resolveOverheadDateRange,
} from '../../utils/overheadDateFilter';

interface OverheadTableProps {
  expenses: OverheadExpense[];
  /** Count of currently-active payroll positions, used to scale per-employee rows. */
  activeHeadcount?: number;
  onEdit: (expense: OverheadExpense) => void;
  onAdd: () => void;
  readOnly?: boolean;
  opexBudgets?: OpexBudgets;
  onBudgetedChange?: (
    group: PurchaseOverheadGroup,
    amountUsd: number | null,
    interval: OpexBudgetInterval,
  ) => void;
  onMoveToGroup?: (expenseId: string, group: PurchaseOverheadGroup | null) => void;
}

const OPEX_DRAG_TYPE = 'application/x-lctf-opex-expense';
const UNGROUPED = 'ungrouped' as const;
type OpexSectionId = PurchaseOverheadGroup | typeof UNGROUPED;

type SortKey = 'name' | 'frequency' | 'amount' | 'monthly' | 'day' | 'end';
type SortDir = 'asc' | 'desc';

const FREQUENCY_ORDER = Object.fromEntries(OVERHEAD_FREQUENCIES.map((f, i) => [f, i])) as Record<string, number>;

/** Monthly and annual first so Juliana can scan those separately inside a group. */
const FREQUENCY_BANDS: OverheadFrequency[] = [
  'monthly',
  'annually',
  'quarterly',
  'weekly',
  'biweekly',
  'daily',
  'one-time',
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatDate = (d: Date | null | undefined): string => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const EXPENSE_GRID = 'minmax(0,2.2fr) minmax(0,1fr) minmax(0,0.9fr) minmax(0,0.9fr) minmax(88px,1.15fr) minmax(0,1.1fr) 72px';
const DRAG_SCROLL_EDGE = 72;
const DRAG_SCROLL_MAX = 28;

function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement || null;
  while (node) {
    const style = window.getComputedStyle(node);
    const canScroll = /(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow);
    if (canScroll && node.scrollHeight > node.clientHeight + 4) return node;
    node = node.parentElement;
  }
  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : document.documentElement;
}

const OverheadTable: React.FC<OverheadTableProps> = ({
  expenses,
  activeHeadcount = 0,
  onEdit,
  onAdd,
  readOnly = false,
  opexBudgets = emptyOpexBudgets(),
  onBudgetedChange,
  onMoveToGroup,
}) => {
  const now = useMemo(() => new Date(), []);
  const [preset, setPreset] = useState<OverheadDatePreset>('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [dayBucket, setDayBucket] = useState<OverheadDayBucket>('any');
  const [exactDay, setExactDay] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [dropTarget, setDropTarget] = useState<OpexSectionId | null>(null);
  const [budgetDrafts, setBudgetDrafts] = useState<Record<string, string>>({});
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const pointerYRef = useRef(0);

  useEffect(() => {
    if (!dragging) return;
    const scroller = findScrollParent(rootRef.current);
    if (!scroller) return;
    let frame = 0;
    const tick = () => {
      const rect = scroller.getBoundingClientRect();
      const y = pointerYRef.current;
      const topDist = y - rect.top;
      const bottomDist = rect.bottom - y;
      let delta = 0;
      if (topDist < DRAG_SCROLL_EDGE) {
        delta = -Math.ceil(((DRAG_SCROLL_EDGE - topDist) / DRAG_SCROLL_EDGE) * DRAG_SCROLL_MAX);
      } else if (bottomDist < DRAG_SCROLL_EDGE) {
        delta = Math.ceil(((DRAG_SCROLL_EDGE - bottomDist) / DRAG_SCROLL_EDGE) * DRAG_SCROLL_MAX);
      }
      if (delta) scroller.scrollTop += delta;
      frame = window.requestAnimationFrame(tick);
    };
    const onDragOver = (event: DragEvent) => {
      pointerYRef.current = event.clientY;
    };
    const stop = () => setDragging(false);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragend', stop);
    document.addEventListener('drop', stop);
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragend', stop);
      document.removeEventListener('drop', stop);
    };
  }, [dragging]);

  const range = useMemo(
    () => resolveOverheadDateRange(preset, fromInputDate(customStart), fromInputDate(customEnd), now),
    [preset, customStart, customEnd, now],
  );
  const exactDayNum = exactDay ? Number(exactDay) : null;
  const filteredExpenses = useMemo(
    () => filterOverheadByDate(expenses, range, dayBucket, Number.isFinite(exactDayNum) ? exactDayNum : null, now),
    [expenses, range, dayBucket, exactDayNum, now],
  );

  const rangeIsBounded = !!(range.start || range.end);

  const decorated = useMemo(() => filteredExpenses.map((expense) => {
    const dates = rangeIsBounded
      ? overheadOccurrencesInRange(expense, range.start, range.end, now)
      : [];
    const monthly = overheadMonthlyContribution(expense, now, activeHeadcount);
    const periodAmount = rangeIsBounded
      ? dates.length * occurrenceAmount(expense, activeHeadcount)
      : monthly;
    const end = expense.endDate ? new Date(expense.endDate).getTime() : Number.POSITIVE_INFINITY;
    return {
      expense,
      monthly,
      periodAmount,
      day: resolveOverheadRecurringDay(expense),
      end,
      chargeCount: dates.length,
      firstCharge: dates[0]?.getTime() ?? Number.POSITIVE_INFINITY,
      dates,
    };
  }), [filteredExpenses, rangeIsBounded, range, now, activeHeadcount]);

  const grouped = useMemo(() => {
    const m: Record<OpexSectionId, typeof decorated> = {
      shop_tools_small_equipment: [],
      facility_office_improvements: [],
      safety_consumables: [],
      software_admin: [],
      training_crew_enablement: [],
      crew_investment: [],
      marketing: [],
      vehicles: [],
      ungrouped: [],
    };
    for (const row of decorated) {
      const group = row.expense.opexGroup;
      if (isPurchaseOverheadGroup(group)) m[group].push(row);
      else m.ungrouped.push(row);
    }
    const factor = sortDir === 'asc' ? 1 : -1;
    const cmp = (a: (typeof decorated)[number], b: (typeof decorated)[number]) => {
      let n = 0;
      if (sortKey === 'name') n = a.expense.name.localeCompare(b.expense.name);
      else if (sortKey === 'frequency') {
        n = (FREQUENCY_ORDER[a.expense.frequency] ?? 0) - (FREQUENCY_ORDER[b.expense.frequency] ?? 0);
      } else if (sortKey === 'amount') n = a.expense.amount - b.expense.amount;
      else if (sortKey === 'monthly') n = a.periodAmount - b.periodAmount;
      else if (sortKey === 'day') {
        const aMonth = a.expense.frequency === 'annually' ? resolveOverheadRenewalMonth(a.expense) : 0;
        const bMonth = b.expense.frequency === 'annually' ? resolveOverheadRenewalMonth(b.expense) : 0;
        n = (aMonth - bMonth) || (a.day - b.day);
      }
      else if (sortKey === 'end') {
        n = rangeIsBounded
          ? (a.chargeCount - b.chargeCount) || (a.firstCharge - b.firstCharge)
          : a.end - b.end;
      }
      if (n === 0) n = a.expense.name.localeCompare(b.expense.name);
      return n * factor;
    };
    (Object.keys(m) as OpexSectionId[]).forEach(key => m[key].sort(cmp));
    return m;
  }, [decorated, sortKey, sortDir, rangeIsBounded]);

  const commitBudgeted = (group: PurchaseOverheadGroup, nextInterval?: OpexBudgetInterval) => {
    if (!onBudgetedChange) return;
    const current = opexBudgets.groups[group];
    const interval = nextInterval || current?.interval || 'monthly';
    const raw = budgetDrafts[group];
    if (raw === undefined && !nextInterval) return;
    if (raw === undefined) {
      onBudgetedChange(group, current?.amountUsd ?? null, interval);
      return;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      onBudgetedChange(group, null, interval);
      return;
    }
    const n = Number(trimmed.replace(/[$,]/g, ''));
    onBudgetedChange(group, Number.isFinite(n) && n >= 0 ? n : null, interval);
  };

  const periodTotal = useMemo(
    () => decorated.reduce((sum, row) => sum + row.periodAmount, 0),
    [decorated],
  );

  const hasActiveFilter = preset !== 'all' || dayBucket !== 'any';

  const clearFilters = () => {
    setPreset('all');
    setCustomStart('');
    setCustomEnd('');
    setDayBucket('any');
    setExactDay('');
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'name' || key === 'frequency' ? 'asc' : 'desc');
  };

  const SortBtn: React.FC<{ field: SortKey; label: string; right?: boolean }> = ({ field, label, right }) => {
    const active = sortKey === field;
    return (
      <button
        type="button"
        className={`fin-th-sort${right ? ' right' : ''}${active ? ' active' : ''}`}
        onClick={() => handleSort(field)}
        aria-label={`Sort by ${label}${active ? `, ${sortDir === 'asc' ? 'ascending' : 'descending'}` : ''}`}
      >
        {label}
        <span className={`fin-th-sort-arrow${active && sortDir === 'desc' ? ' desc' : ''}${active ? ' on' : ''}`} aria-hidden>
          <ArrowUpDown size={10} />
        </span>
      </button>
    );
  };

  const emptyMessage = expenses.length === 0
    ? {
        title: 'No OpEx expenses yet',
        sub: 'Add known recurring bills, then set a Budgeted envelope on each group for unknown PO spend.',
      }
    : {
        title: 'No expenses match these dates',
        sub: 'Widen the range, clear the day filter, or switch back to all dates.',
      };

  return (
    <div className="fin-overhead" ref={rootRef}>
      <div className="fin-oh-filters" role="region" aria-label="Filter overhead by date">
        <div className="fin-oh-filters-row">
          <label className="fin-oh-date-field">
            <span>Date range</span>
            <select
              className="fin-oh-range-select"
              value={preset}
              aria-label="Filter overhead by date range"
              onChange={(e) => setPreset(e.target.value as OverheadDatePreset)}
            >
              {OVERHEAD_DATE_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </label>
          {preset !== 'all' && (
            <span className="fin-oh-range-label">{formatOverheadRangeLabel(range)}</span>
          )}
          {preset === 'custom' && (
            <div className="fin-oh-custom-dates">
              <label className="fin-oh-date-field">
                <span>From</span>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
              </label>
              <label className="fin-oh-date-field">
                <span>To</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </label>
            </div>
          )}
          <label className="fin-oh-date-field">
            <span>Recurring day</span>
            <select
              value={dayBucket}
              onChange={(e) => setDayBucket(e.target.value as OverheadDayBucket)}
            >
              {OVERHEAD_DAY_BUCKETS.map((b) => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </select>
          </label>
          {dayBucket === 'exact' && (
            <label className="fin-oh-date-field">
              <span>Day</span>
              <input
                type="number"
                min={1}
                max={31}
                value={exactDay}
                onChange={(e) => setExactDay(e.target.value)}
                placeholder="28"
              />
            </label>
          )}
          {hasActiveFilter && (
            <button type="button" className="fin-oh-clear" onClick={clearFilters}>
              <X size={12} /> Clear
            </button>
          )}
          <span className="fin-oh-filter-stats">
            {`${filteredExpenses.length} expense${filteredExpenses.length === 1 ? '' : 's'}`}
            <span className="fin-opex-split-known">
              {`Known ${formatCurrency(periodTotal)}${rangeIsBounded ? ' in range' : '/mo'}`}
            </span>
            <span className="fin-opex-split-budgeted">
              {`Budgeted ${formatCurrency(totalBudgetedOpexMonthly(opexBudgets))}/mo`}
            </span>
          </span>
        </div>
      </div>

      {filteredExpenses.length === 0 ? (
        <div className="fin-table-wrap">
          <div className="fin-table-empty">
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--pm-text-secondary)' }}>{emptyMessage.title}</p>
            <span className="fin-table-empty-sub">{emptyMessage.sub}</span>
            {!readOnly && expenses.length === 0 && (
              <div style={{ marginTop: 14 }}>
                <button type="button" className="fin-primary-btn" onClick={onAdd}>
                  <Plus size={14} /> New expense
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="fin-table-wrap">
          <div className="fin-table-header" style={{ gridTemplateColumns: EXPENSE_GRID }} role="row">
            <SortBtn field="name" label="Expense" />
            <SortBtn field="frequency" label="Frequency" />
            <SortBtn field="amount" label="Amount" right />
            <SortBtn field="monthly" label={rangeIsBounded ? 'In range' : 'Monthly'} right />
            <SortBtn field="day" label="Bills on" />
            <SortBtn field="end" label={rangeIsBounded ? 'Charges' : 'End'} />
            <span className="fin-th right">Actions</span>
          </div>

          {([...PURCHASE_OVERHEAD_GROUPS.map(g => g.id), UNGROUPED] as OpexSectionId[]).map((sectionId) => {
            const items = grouped[sectionId];
            const isUngrouped = sectionId === UNGROUPED;
            const group = isUngrouped ? null : sectionId;
            const overheadTotal = items.reduce((sum, row) => sum + row.periodAmount, 0);
            const envelope = group ? opexBudgets.groups[group] : undefined;
            const budgetedUsd = envelope?.amountUsd ?? null;
            const budgetInterval = envelope?.interval || 'monthly';
            const budgetedMonthly = budgetedMonthlyEquivalent(envelope);
            const budgetedLabel = envelope ? formatOpexBudgetAmount(envelope) : null;
            const groupTotal = overheadTotal + (budgetedMonthly || 0);
            const label = isUngrouped ? 'Ungrouped' : PURCHASE_OVERHEAD_GROUPS.find(g => g.id === group)?.label || sectionId;
            return (
              <div
                key={sectionId}
                className={`fin-table-group${dropTarget === sectionId ? ' fin-opex-drop' : ''}`}
                onDragOver={event => {
                  if (readOnly || !onMoveToGroup) return;
                  event.preventDefault();
                  setDropTarget(sectionId);
                }}
                onDragLeave={() => setDropTarget(current => (current === sectionId ? null : current))}
                onDrop={event => {
                  if (readOnly || !onMoveToGroup) return;
                  event.preventDefault();
                  setDropTarget(null);
                  const id = event.dataTransfer.getData(OPEX_DRAG_TYPE);
                  if (id) onMoveToGroup(id, group);
                }}
              >
                <div
                  className="fin-table-group-header fin-opex-section-bar"
                  title={
                    isUngrouped
                      ? 'Recurring lines not yet assigned to an OpEx bucket. Drag onto a group.'
                      : `Known ${formatCurrency(overheadTotal)} · Budgeted ${budgetedLabel || 'not set'}`
                  }
                >
                  <div className="fin-opex-group-title">
                    <span className="fin-table-group-color" />
                    <span className="fin-table-group-name">{label}</span>
                    <span className="fin-table-group-count">{items.length}</span>
                  </div>
                  {!isUngrouped && (
                    <div className="fin-opex-split-totals">
                      <span className="fin-opex-split-known" title="Known recurring bills in this group">
                        Known {formatCurrency(overheadTotal)}{rangeIsBounded ? '' : '/mo'}
                      </span>
                      <span className="fin-opex-split-budgeted" title="Budgeted PO envelope, as monthly burn">
                        Budgeted {budgetedMonthly == null ? 'not set' : `${formatCurrency(budgetedMonthly)}${rangeIsBounded ? '' : '/mo'}`}
                      </span>
                    </div>
                  )}
                  <span className="fin-table-group-total">
                    {formatCurrency(isUngrouped ? overheadTotal : groupTotal)}{rangeIsBounded ? '' : '/mo'}
                  </span>
                </div>
                {(sortKey === 'frequency' && sortDir === 'desc'
                  ? [...FREQUENCY_BANDS].reverse()
                  : FREQUENCY_BANDS
                ).map((frequency) => {
                  const bandRows = items.filter(row => row.expense.frequency === frequency);
                  if (bandRows.length === 0) return null;
                  const bandTotal = bandRows.reduce((sum, row) => sum + row.periodAmount, 0);
                  return (
                    <div key={frequency} className="fin-oh-freq-band">
                      <div className="fin-oh-freq-header">
                        <span className="fin-oh-freq-name">{OVERHEAD_FREQUENCY_LABELS[frequency]}</span>
                        <span className="fin-table-group-count">{bandRows.length}</span>
                        <span className="fin-oh-freq-total">
                          {formatCurrency(bandTotal)}{rangeIsBounded ? '' : '/mo'}
                        </span>
                      </div>
                      {bandRows.map((row) => {
                        const e = row.expense;
                        const isPerEmp = e.scalesWith === 'per_active_employee';
                        return (
                          <div
                            key={e.id}
                            className={`fin-table-row${e.isActive ? '' : ' inactive'}`}
                            style={{ gridTemplateColumns: EXPENSE_GRID }}
                            draggable={!readOnly && !!onMoveToGroup}
                            onDragStart={event => {
                              event.dataTransfer.setData(OPEX_DRAG_TYPE, e.id);
                              event.dataTransfer.effectAllowed = 'move';
                              pointerYRef.current = event.clientY;
                              setDragging(true);
                            }}
                            onDragEnd={() => {
                              setDragging(false);
                              setDropTarget(null);
                            }}
                            onClick={readOnly ? undefined : () => onEdit(e)}
                            role={readOnly ? undefined : 'button'}
                            tabIndex={readOnly ? undefined : 0}
                            onKeyDown={readOnly ? undefined : (evt) => {
                              if (evt.key === 'Enter') onEdit(e);
                            }}
                          >
                            <div className="fin-cell">
                              <div className="fin-cell-primary">
                                {e.name}
                                {isPerEmp && (
                                  <span
                                    className="fin-peremp-badge"
                                    title={`Per employee — scaled by ${activeHeadcount} active position${activeHeadcount === 1 ? '' : 's'}`}
                                  >
                                    Per emp
                                  </span>
                                )}
                              </div>
                              {(e.vendor || e.category) && (
                                <div className="fin-cell-sub">
                                  {OVERHEAD_CATEGORY_LABELS[e.category]}
                                  {e.vendor ? ` · ${e.vendor}` : ''}
                                </div>
                              )}
                            </div>
                            <div className="fin-cell">
                              <span className="fin-pill">{OVERHEAD_FREQUENCY_LABELS[e.frequency]}</span>
                            </div>
                            <div className="fin-cell fin-cell-money">
                              {formatCurrency(e.amount)}
                              {isPerEmp && <div className="fin-cell-sub" style={{ textAlign: 'right' }}>per employee</div>}
                            </div>
                            <div className="fin-cell fin-cell-money">
                              {formatCurrency(row.periodAmount)}
                              {isPerEmp && !rangeIsBounded && (
                                <div className="fin-cell-sub" style={{ textAlign: 'right' }}>
                                  × {activeHeadcount}
                                </div>
                              )}
                            </div>
                            <div className="fin-cell fin-cell-muted">
                              {formatOverheadBillOn(e)}
                            </div>
                            <div className="fin-cell fin-cell-muted">
                              {rangeIsBounded ? formatOccurrenceSummary(row.dates) : formatDate(e.endDate)}
                            </div>
                            <div className="fin-row-actions">
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="fin-icon-btn"
                                  onClick={(evt) => { evt.stopPropagation(); onEdit(e); }}
                                  aria-label={`Edit ${e.name}`}
                                >
                                  <Pencil size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {!isUngrouped && (
                  <div
                    className="fin-table-row fin-opex-budget-row"
                    style={{ gridTemplateColumns: EXPENSE_GRID }}
                    title="Budgeted PO envelope for unknown or discretionary spend. Does not add a recurring bill."
                  >
                    <div className="fin-cell">
                      <div className="fin-cell-primary">
                        Budgeted envelope
                      </div>
                      <div className="fin-cell-sub">Unknown / discretionary POs — not a known recurring bill</div>
                    </div>
                    <div className="fin-cell">
                      {readOnly || !onBudgetedChange ? (
                        <span className="fin-pill">{opexBudgetIntervalMeta(budgetInterval).label}</span>
                      ) : (
                        <select
                          className="fin-opex-interval-select"
                          aria-label={`${label} budget interval`}
                          value={budgetInterval}
                          onChange={event => commitBudgeted(sectionId, event.target.value as OpexBudgetInterval)}
                          onClick={event => event.stopPropagation()}
                        >
                          {OPEX_BUDGET_INTERVALS.map(option => (
                            <option key={option.id} value={option.id}>{option.label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="fin-cell fin-cell-money">
                      {readOnly || !onBudgetedChange ? (
                        budgetedUsd == null ? '—' : formatCurrency(budgetedUsd)
                      ) : (
                        <input
                          className="fin-opex-budget-input"
                          inputMode="decimal"
                          aria-label={`${label} budgeted amount`}
                          placeholder="0"
                          value={budgetDrafts[sectionId] ?? (budgetedUsd == null ? '' : String(budgetedUsd))}
                          onChange={event => setBudgetDrafts(prev => ({ ...prev, [sectionId]: event.target.value }))}
                          onBlur={() => commitBudgeted(sectionId)}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              event.currentTarget.blur();
                            }
                          }}
                          onClick={event => event.stopPropagation()}
                        />
                      )}
                    </div>
                    <div className="fin-cell fin-cell-money">
                      {budgetedMonthly == null ? '—' : formatCurrency(budgetedMonthly)}
                      {budgetedMonthly != null && !rangeIsBounded && (
                        <div className="fin-cell-sub" style={{ textAlign: 'right' }}>/mo in burn</div>
                      )}
                    </div>
                    <div className="fin-cell fin-cell-muted">—</div>
                    <div className="fin-cell fin-cell-muted">Envelope</div>
                    <div className="fin-row-actions" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OverheadTable;
